import {createTheme} from '@mui/material/styles';
import {nbNO} from '@mui/material/locale';
import {nbNO as dataGridTranslations} from '@mui/x-data-grid/locales'
import { DataGridProps } from '@mui/x-data-grid';
import '@mui/x-date-pickers/themeAugmentation';

interface CustomColorTokens {
    yourActive: string;
    yourInactive: string;
    replyActive: string;
    replyInactive: string;
    metaText: string;
}

interface SeverityTokens {
    critical: string;
    high: string;
    medium: string;
    low: string;
}

interface EventTypeTokens {
    fellesfeil: string;
    planlagt: string;
    generell: string;
}

interface StatusTokens {
    active: string;
    noAffected: string;
    deactivated: string;
}

declare module '@mui/material/styles/createPalette' {
    interface Palette {
        accent: Palette['primary'];
        bubble: CustomColorTokens;
        severity: SeverityTokens;
        eventType: EventTypeTokens;
        status: StatusTokens;
    }
    interface PaletteOptions {
        accent: PaletteOptions['primary'];
        bubble?: CustomColorTokens;
        severity?: SeverityTokens;
        eventType?: EventTypeTokens;
        status?: StatusTokens;
    }
}

declare module '@mui/material/styles' {
    interface Components {
        MuiDataGrid?: {
            styleOverrides?: {
                [key: string]: any;
            };
            defaultProps?: Partial<DataGridProps>;
        };
    }
}

const lightPalette = {
    primary: {
        light: '#82c0f440',
        main: '#3a41ff',
        dark: '#2c31bf',
    },
    secondary: {
        /* light: '#d3fffa', */
        light: '#19bfac40',
        main: '#19bfac',
        dark: '#118073',
    },
    text: {
        primary: '#101820',
        secondary: '#40464D',
        disabled: '#0000008c',
    },
    background: {
        default: '#FFFFFF',
        paper: '#F7F7FF',
    },
    accent: {
        main: '#00069B',
        light: '#B3B4E1',
        dark: '#000489',
    },
    success: {
        main: '#21ac04',
        light: '#7acd68a3',
        dark: '#198103',
    },
    warning: {
        main: '#e17000',
        light: '#eda966a3',
        dark: '#a95400',
    },
    error: {
        main: '#cb0000',
        light: '#e06666a3',
        dark: '#980000',
    },
    info: {
        light: '#d3a7ffa3',
        main: '#1d4769',
        dark: '#0C3D80',
    },
    divider: '#C7C7CC',
    action: {
        active: '#2b2b2b',
        hover: 'rgba(130, 192, 244, 0.1)',
        selected: '#82c0f440',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        focus: '#8C8C8C',
    },
    bubble: {
        yourActive: '#b9dcf9',
        yourInactive: '#82c0f440',
        replyActive: '#D4D5D4',
        replyInactive: '#E7E7E7',
        metaText: '#40464D',
    },
    severity: {
        critical: '#CB0000',
        high: '#E17000',
        medium: '#B8960A',
        low: '#2563EB',
    },
    eventType: {
        fellesfeil: '#F44336',
        planlagt: '#a52dba',
        generell: '#3f5bb5',
    },
    status: {
        active: '#21ac04',
        noAffected: '#e17000',
        deactivated: '#cb0000',
    },
};

// EB Light Theme
export const lightTheme = createTheme(
    {
        palette: {
            ...lightPalette,
            mode: 'light',
            eidsiva: {
                main: '#3a41ff',
                light: '#82c0f440',
                dark: '#2c31bf',
            },
        },
        typography: {
            fontFamily: "'Aeonik', 'Poppins', 'Arial'",
            fontSize: 16,
            h1: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '5rem',
                lineHeight: '125%',
            },
            h2: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '3rem',
                lineHeight: '125%',
            },
            h3: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '2.5rem',
                lineHeight: '125%',
            },
            h4: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.75rem',
            },
            h5: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.5rem',
                lineHeight: '150%',
            },
            h6: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.15rem',
                lineHeight: '150%',
            },
            button: {
                fontFamily: "'Aeonik', 'Poppins'",
                textTransform: 'none',
            },
            subtitle1: {
                fontFamily: "'Aeonik Bold', 'Poppins'",
                fontSize: '1.35rem',
                lineHeight: '150%',
            },
            subtitle2: {
                fontFamily: "'Aeonik Bold', 'Poppins'",
                fontSize: '1.35rem',
                lineHeight: '150%',
                textTransform: 'uppercase',
            },
            body1: {
                lineHeight: '150%',
            },
            body2: {
                lineHeight: '150%',
            },
            caption: {
                fontSize: '0.9rem',
                lineHeight: '150%',
            },
            overline: {
                fontFamily: 'Aeonik Light',
                lineHeight: '150%',
            },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                    },
                    outlined: {
                        borderWidth: 1.5,
                        '&:hover': {
                            borderWidth: 1.5,
                        },
                    },
                    text: {
                        '&.MuiButton-colorPrimary': {
                            backgroundColor: '#82c0f440', // .25 % opacity
                            '&:hover': {
                                backgroundColor: '#3d86fc80', // .50 % opacity
                            },
                        },
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        color: lightPalette.primary.main,
                        borderColor: lightPalette.background.paper,
                        '&.Mui-selected': {
                            backgroundColor: lightPalette.background.paper,

                            '&:hover': {
                                backgroundColor: lightPalette.background.paper,
                            },
                        },
                        '&.Mui-disabled': {
                            border: 0,

                            '&:hover': {
                                backgroundColor: lightPalette.background.paper,
                            },
                        },
                    },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        backgroundColor: lightPalette.primary.light,
                    },
                    grouped: {
                        '&:not(:first-of-type)': {
                            borderRadius: 0,
                        },
                        '&:first-of-type': {
                            borderRadius: 0,
                        }
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        display: 'inline-flex',
                        textUnderlinePosition: 'under',
                        textUnderlineOffset: 0.5,
                        textDecorationThickness: 1.5,
                        textDecorationColor: 'transparent',
                        transition: 'all 0.2s ease-in',
                        fontFamily: "'Aeonik', 'Poppins'",
                    },
                    underlineHover: {
                        '&:hover': {
                            textDecorationColor: '#3a41ff',
                            textUnderlinePosition: 'under',
                            textUnderlineOffset: 0.5,
                            textDecorationThickness: 1.25,
                        },
                    },
                    underlineAlways: {
                        textDecorationColor: '#82c0f440',
                        '&:hover': {
                            textDecorationColor: '#3a41ff',
                            textUnderlinePosition: 'under',
                            textUnderlineOffset: 0.5,
                            textDecorationThickness: 1.5,
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: lightPalette.background.paper,
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: lightPalette.background.default,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: lightPalette.background.paper,
                        color: lightPalette.text.primary,
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    panelHeader: {
                        display: 'none',
                    },
                    panelFooter: {
                        display: 'none',
                    },
                    root: {
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            justifyContent: 'center',
                            borderTop: `1px solid ${lightPalette.divider}`,
                        },
                        border: `1px solid ${lightPalette.divider}`,
                        borderBottomRightRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        borderTopLeftRadius: '8px',
                        boxShadow: '0',
                        backgroundColor: lightPalette.background.paper,
                    },

                },
                defaultProps: {
                    localeText: {
                        ...dataGridTranslations.components.MuiDataGrid.defaultProps.localeText,
                        noRowsLabel: 'Ingen hendelser funnet',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        backgroundColor: lightPalette.background.paper,
                        '&:hover': {
                            backgroundColor: lightPalette.background.paper,
                        },
                        '&.Mui-focused': {
                            backgroundColor: lightPalette.background.paper,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.primary.light,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.primary.main,
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': {
                            color: lightPalette.text.primary,
                        },
                    },
                },
            },
            MuiPickersOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        '& .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.divider,
                        },
                        '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.primary.light,
                        },
                        '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: lightPalette.primary.main,
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        borderColor: lightPalette.secondary.light,
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        shapeOutside: '8px',
                        shapeMargin: '8px',
                        backgroundColor: lightPalette.background.paper,
                    },
                },
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        borderRadius: '0 0 8px 8px',
                        padding: "16px 16px 16px",
                        backgroundColor: lightPalette.background.paper,
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    noOptions: {
                        color: lightPalette.text.primary,
                        fontSize: '1rem',
                    },
                    inputRoot: {
                        fontSize: '1rem',
                    },
                    input: {
                        fontSize: '1rem',
                    },
                    listbox: {
                        fontSize: '1rem',
                        borderRadius: '8px',
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: lightPalette.primary.main,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        '&.Mui-selected': {
                            color: lightPalette.primary.main,
                            backgroundColor: lightPalette.primary.light,
                        },
                    },
                },
            },
        },
    },
    nbNO
);

const darkPalette = {
    primary: {
        main: '#82C0F4',
        dark: '#3D5AFE',
        light: '#64B5F6',
        contrastText: '#000',
    },
    secondary: {
        main: '#4DD0BD',
        dark: '#19bfac',
        light: '#80E0D2',
        contrastText: '#000',
    },
    error: {
        main: '#E57373',
        dark: '#C62828',
        light: '#EF5350',
        contrastText: '#000',
    },
    warning: {
        main: '#FFB74D',
        dark: '#FF9800',
        light: '#FFD54F',
        contrastText: '#000',
    },
    info: {
        main: '#4FC3F7',
        dark: '#03A9F4',
        light: '#81D4FA',
        contrastText: '#000',
    },
    success: {
        main: '#81C784',
        dark: '#388E3C',
        light: '#4CAF50',
        contrastText: '#000',
    },
    text: {
        primary: '#F7F7FF',
        secondary: '#82C0F4',
        disabled: '#C7C7CC',
    },
    background: {
        default: '#121212',
        paper: '#1E1E1E'
    },
    divider: '#666666',
    action: {
        active: '#FFFFFF',
        hover: 'rgba(130, 192, 244, 0.1)',
        selected: '#82c0f440',
        disabled: '#666666',
        disabledBackground: '#222222',
        focus: '#8C8C8C',
    },
    accent: {
        main: '#FF9800',
    },
    bubble: {
        yourActive: '#1d4769',
        yourInactive: '#223f57',
        replyActive: '#40464D',
        replyInactive: '#31363C',
        metaText: '#9FA3A6',
    },
    severity: {
        critical: '#EF5350',
        high: '#FFA726',
        medium: '#FFCA28',
        low: '#64B5F6',
    },
    eventType: {
        fellesfeil: '#EF5350',
        planlagt: '#B07CC6',
        generell: '#64B5F6',
    },
    status: {
        active: '#81C784',
        noAffected: '#FFB74D',
        deactivated: '#E57373',
    },
};

export const darkTheme = createTheme(
    {
        palette: {
            ...darkPalette,
            mode: 'dark',
            eidsiva: {
                main: '#3a41ff',
                light: '#82c0f440',
                dark: '#2c31bf',
            },
        },
        typography: {
            fontFamily: "'Aeonik', 'Poppins', 'Arial'",
            fontSize: 16,
            h1: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '5rem',
                lineHeight: '125%',
            },
            h2: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '3rem',
                lineHeight: '125%',
            },
            h3: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '2.5rem',
                lineHeight: '125%',
            },
            h4: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.75rem',
            },
            h5: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.5rem',
                lineHeight: '150%',
            },
            h6: {
                fontFamily: "'Aeonik', 'Poppins'",
                fontSize: '1.15rem',
                lineHeight: '150%',
            },
            button: {
                fontFamily: "'Aeonik', 'Poppins'",
                textTransform: 'none',
            },
            subtitle1: {
                fontFamily: "'Aeonik Bold', 'Poppins'",
                fontSize: '1.35rem',
                lineHeight: '150%',
            },
            subtitle2: {
                fontFamily: "'Aeonik Bold', 'Poppins'",
                fontSize: '1.35rem',
                lineHeight: '150%',
                textTransform: 'uppercase',
            },
            body1: {
                lineHeight: '150%',
            },
            body2: {
                lineHeight: '150%',
            },
            caption: {
                fontSize: '0.9rem',
                lineHeight: '150%',
            },
            overline: {
                fontFamily: 'Aeonik Light',
                lineHeight: '150%',
            },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                    },
                    outlined: {
                        borderWidth: 1.5,
                        '&:hover': {
                            borderWidth: 1.5,
                        },
                    },
                    text: {
                        '&.MuiButton-colorPrimary': {
                            backgroundColor: '#82c0f440', // .25 % opacity
                            '&:hover': {
                                backgroundColor: '#3d86fc80', // .50 % opacity
                            },
                        },
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        borderColor: darkPalette.background.paper,
                        color: darkPalette.primary.main,
                        '&.Mui-selected': {
                            color: "white",
                            backgroundColor: darkPalette.background.paper,

                            '&:hover': {
                                backgroundColor: darkPalette.background.paper,
                            },
                        },
                    },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#383838',
                        border: 0,
                    },
                    grouped: {
                        '&:not(:first-of-type)': {
                            borderRadius: '0px',
                        },
                        '&:first-of-type': {
                            borderRadius: '0px',
                        }
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        display: 'inline-flex',
                        textUnderlinePosition: 'under',
                        textUnderlineOffset: 0.5,
                        textDecorationThickness: 1.5,
                        textDecorationColor: 'transparent',
                        transition: 'all 0.2s ease-in',
                        fontFamily: "'Aeonik', 'Poppins'",
                        color: darkPalette.text.primary,
                    },
                    underlineHover: {
                        '&:hover': {
                            textDecorationColor: '#3a41ff',
                            textUnderlinePosition: 'under',
                            textUnderlineOffset: 0.5,
                            textDecorationThickness: 1.25,
                        },
                    },
                    underlineAlways: {
                        textDecorationColor: '#82c0f440',
                        '&:hover': {
                            textDecorationColor: '#3a41ff',
                            textUnderlinePosition: 'under',
                            textUnderlineOffset: 0.5,
                            textDecorationThickness: 1.5,
                        },
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    h1: {
                        color: darkPalette.text.primary,
                    },
                    h2: {
                        color: darkPalette.text.primary,
                    },
                    h3: {
                        color: darkPalette.text.primary,
                    },
                    h4: {
                        color: darkPalette.text.primary,
                    },
                    h5: {
                        color: darkPalette.text.primary,
                    },
                    h6: {
                        color: darkPalette.text.primary,
                    },
                    subtitle1: {
                        color: darkPalette.text.primary,
                    },
                    subtitle2: {
                        color: darkPalette.text.secondary,
                    },
                    body1: {
                        color: darkPalette.text.primary,
                    },
                    body2: {
                        color: darkPalette.text.primary,
                    },
                    caption: {
                        color: darkPalette.text.secondary,
                    },
                    overline: {
                        color: darkPalette.text.secondary,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkPalette.background.paper,
                        color: darkPalette.text.primary,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkPalette.background.paper,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: darkPalette.background.paper,
                        color: darkPalette.text.primary,
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    panelHeader: {
                        display: 'none',
                    },
                    panel: {
                        flexFlow: 'column',
                        flexDirection: 'column',
                        display: 'flex',
                        filter: 'none',
                    },
                    panelFooter: {
                        display: 'none',
                    },
                    root: {
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            justifyContent: 'center',
                            borderTop: `1px solid ${darkPalette.divider}`,
                        },
                        border: `1px solid ${darkPalette.divider}`,
                        borderBottomRightRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        borderTopLeftRadius: '8px',
                        boxShadow: '0',
                        backgroundColor: darkPalette.background.paper,
                    },
                },
                defaultProps: {
                    localeText: {
                        ...dataGridTranslations.components.MuiDataGrid.defaultProps.localeText,
                        noRowsLabel: 'Ingen hendelser funnet',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& label.Mui-focused': {
                            color: 'white',
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: darkPalette.primary.main,
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: darkPalette.divider,
                            },
                            '&:hover fieldset': {
                                borderColor: darkPalette.primary.light,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: darkPalette.primary.main,
                            },
                        },
                        borderRadius: '8px',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        backgroundColor: darkPalette.background.paper,
                        '&:hover': {
                            backgroundColor: darkPalette.background.paper,
                        },
                        '&.Mui-focused': {
                            backgroundColor: darkPalette.background.paper,
                        },
                        '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.divider,
                        },
                        '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.primary.light,
                        },
                        '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.primary.main,
                        },

                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: darkPalette.text.primary,
                        '&.Mui-focused': {
                            color: darkPalette.text.primary,
                        },
                    },
                },
            },
            MuiPickersOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        backgroundColor: darkPalette.background.paper,
                        '&:hover': {
                            backgroundColor: darkPalette.background.paper,
                        },
                        '&.Mui-focused': {
                            backgroundColor: darkPalette.background.paper,
                        },
                        '& .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.divider,
                        },
                        '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.primary.light,
                        },
                        '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: darkPalette.primary.main,
                        },
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        shapeOutside: '8px',
                        shapeMargin: '8px',
                        backgroundColor: darkPalette.background.paper,
                    },
                },
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        borderRadius: '0 0 8px 8px',
                        padding: "16px 16px 16px",
                        backgroundColor: darkPalette.background.paper,
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        scrollbarColor: "#6b6b6b #2b2b2b",
                        "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                            backgroundColor: darkPalette.background.default,
                            backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))",
                            borderRadius: 5,
                            width: 5,
                        },
                        "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                            borderRadius: 5,
                            backgroundColor: "#6b6b6b",
                        },
                        "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
                            backgroundColor: "#959595",
                        },
                        "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
                            backgroundColor: "#959595",
                        },
                        "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#959595",
                        },
                        "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
                            backgroundColor: "#2b2b2b",
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    noOptions: {
                        color: darkPalette.text.primary,
                        fontSize: '1rem',
                    },
                    inputRoot: {
                        fontSize: '1rem',
                    },
                    input: {
                        fontSize: '1rem',
                    },
                    listbox: {
                        fontSize: '1rem',
                        borderRadius: '8px',
                    },
                },
            },

            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: darkPalette.primary.main,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        '&.Mui-selected': {
                            color: darkPalette.primary.main,
                            backgroundColor: darkPalette.background.default,
                        },
                    },
                },
            },
        },
    },
    nbNO
);
